import React, { useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, X } from "lucide-react";
import api, { API_BASE } from "../../lib/api";

/** Absolute URL for uploaded files.
 *  Always go through the backend's `/api/files/*` endpoint. The backend
 *  transparently redirects (307) to Cloudflare R2's CDN when a file has been
 *  migrated, and proxies from legacy Emergent Object Storage otherwise —
 *  so both new AND old uploads keep working. */
export const resolveImage = (url) => {
  if (!url) return "";
  if (url.startsWith("http")) return url;
  const base = API_BASE.replace(/\/api\/?$/, "");
  return `${base}${url}`;
};

const ImageUpload = ({ value, onChange, label = "Görsel", testId, kind = "image", darkPreview = false }) => {
  const [busy, setBusy] = useState(false);
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef(null);
  const isVideo = kind === "video";
  const acceptAttr = isVideo ? "video/*" : kind === "both" ? "image/*,video/*" : "image/*";
  const helperText = isVideo ? "mp4 · webm · mov · m4v (max 30 MB)" : kind === "both" ? "jpg · png · mp4 · webm (max 30 MB)" : "jpg · png · webp · svg (max 10 MB)";
  const promptText = isVideo ? "Video sürükle-bırak ya da tıkla" : "Görseli sürükle-bırak ya da tıkla";
  const guessedIsVideo = (url) => /\.(mp4|webm|mov|ogg|m4v)(\?|#|$)/i.test(url || "");

  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange(data.url);
      toast.success("Yüklendi");
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message);
    } finally {
      setBusy(false);
    }
  };

  const onPick = (e) => {
    const file = e.target.files?.[0];
    if (file) upload(file);
  };
  const onDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) upload(file);
  };

  const preview = resolveImage(value);

  return (
    <div>
      {label ? <div className="text-[0.68rem] tracking-[0.24em] uppercase text-brand-medium mb-2">{label}</div> : null}
      <div
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={onDrop}
        className={`relative w-full border ${dragging ? "border-brand-gold border-dashed" : "border-black/15"} ${darkPreview ? "bg-brand-navy" : "bg-white"}`}
        data-testid={testId}
      >
        {preview ? (
          <div className="relative">
            {isVideo || guessedIsVideo(preview) ? (
              <video src={preview} controls muted playsInline className="w-full max-h-64 object-cover bg-black" />
            ) : (
              <img src={preview} alt="" className="w-full max-h-64 object-contain p-4" />
            )}
            <button
              type="button"
              onClick={() => onChange("")}
              className="absolute top-2 right-2 bg-white/95 p-2 border border-black/10 hover:bg-brand-navy hover:text-white transition-colors"
              aria-label="Temizle"
              data-testid={testId ? `${testId}-clear` : undefined}
            >
              <X size={14} />
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            className="w-full flex flex-col items-center justify-center py-14 text-brand-medium hover:text-brand-navy transition-colors"
            data-testid={testId ? `${testId}-open` : undefined}
          >
            <Upload size={22} strokeWidth={1.2} />
            <div className="mt-3 text-sm">{promptText}</div>
            <div className="mt-1 text-xs text-brand-medium">{helperText}</div>
          </button>
        )}
        <input
          ref={inputRef}
          type="file"
          accept={acceptAttr}
          className="hidden"
          onChange={onPick}
          data-testid={testId ? `${testId}-input` : undefined}
        />
      </div>
      <div className="mt-2 flex items-center gap-3 text-xs text-brand-medium">
        <button
          type="button"
          className="underline hover:text-brand-navy"
          onClick={() => inputRef.current?.click()}
          disabled={busy}
        >
          {busy ? "Yükleniyor…" : "Farklı dosya seç"}
        </button>
        {value ? (
          <>
            <span className="opacity-40">·</span>
            <input
              type="text"
              value={value}
              onChange={(e) => onChange(e.target.value)}
              placeholder="veya URL yapıştır"
              className="flex-1 bg-transparent border-b border-black/10 focus:border-brand-navy focus:outline-none py-1 text-xs"
              data-testid={testId ? `${testId}-url` : undefined}
            />
          </>
        ) : (
          <input
            type="text"
            value={value || ""}
            onChange={(e) => onChange(e.target.value)}
            placeholder="veya URL yapıştır"
            className="flex-1 bg-transparent border-b border-black/10 focus:border-brand-navy focus:outline-none py-1 text-xs"
            data-testid={testId ? `${testId}-url` : undefined}
          />
        )}
      </div>
    </div>
  );
};

export default ImageUpload;
